import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { Field, Th, Td, EmptyYacht } from "./Inventory";
import { SYSTEMS } from "./maintenance/constants";
import { Plus, Trash2, FileText, AlertCircle, Pencil, History, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import FileUploader from "../../components/FileUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";

const CATEGORIES = ["Yacht registration", "Insurance", "Crew documents", "Service reports", "Invoices", "Manuals", "Warranties", "Safety certificates", "Marina contracts", "Permits", "Class documents", "Engine documents", "Other"];
const TECHNICAL_CATS = ["Service reports", "Manuals", "Warranties", "Engine documents"];
const FINANCIAL_CATS = ["Invoices"];
const REMINDER_UNITS = ["days", "weeks", "months"];

const DEFAULT = { name: "", category: CATEGORIES[0], file_path: "", expires_at: "", linked_system: "", supplier: "", supplier_id: "", notes: "", reminder_value: "", reminder_unit: "days" };

// First-page preview next to each document. PDFs get a server-generated
// `<path>.thumb.jpg`; image files preview themselves; everything else (and
// any missing thumb) falls back to a file icon.
const IMG_EXTS = /\.(jpe?g|png|webp|gif|avif|heic)$/i;
function DocThumb({ path, testid }) {
  const thumbPath = !path ? null
    : IMG_EXTS.test(path) ? path
    : path.toLowerCase().endsWith(".pdf") ? `${path}.thumb.jpg`
    : null;
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setSrc(null); setErr(false);
    if (!thumbPath) return;
    let alive = true, blobUrl;
    (async () => {
      try {
        const r = await api.get(`/files/${thumbPath}`, { responseType: "blob" });
        if (!alive) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch { if (alive) setErr(true); }
    })();
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [thumbPath]);
  if (!thumbPath || err) {
    return (
      <div className="w-[44px] h-[56px] rounded-sm border border-zinc-200 bg-zinc-50 flex items-center justify-center shrink-0">
        <FileText className="h-4 w-4 text-zinc-300" />
      </div>
    );
  }
  if (!src) return <div className="w-[44px] h-[56px] rounded-sm border border-zinc-200 bg-zinc-100 animate-pulse shrink-0" />;
  return <img src={src} alt="" data-testid={testid} className="w-[44px] h-[56px] rounded-sm border border-zinc-200 object-cover object-top shrink-0 bg-white" />;
}

export default function Documents() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can, role } = useRights();

  // Per-role document category restrictions (mirrors backend rights.py).
  const allowedCategories = (() => {
    if (role === "engineer" || role === "deckhand") return TECHNICAL_CATS;
    if (role === "accountant") return FINANCIAL_CATS;
    return CATEGORIES;
  })();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);          // doc being edited (null = create)
  const [form, setForm] = useState({ ...DEFAULT, category: allowedCategories[0] });
  const [renewDoc, setRenewDoc] = useState(null);        // doc being renewed
  const [renewForm, setRenewForm] = useState({ file_path: "", expires_at: "" });
  const [historyOpen, setHistoryOpen] = useState({});    // { docId: bool }
  const [confirmDoc, setConfirmDoc] = useState(null);    // doc pending deletion
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/documents?yacht_id=${activeYacht.id}`);
      setItems(data);
    } catch {
      setItems([]);
    }
    try {
      const { data } = await api.get(`/suppliers?yacht_id=${activeYacht.id}`);
      setSuppliers(data || []);
    } catch {
      setSuppliers([]);
    }
  };
  useEffect(() => { setItems([]); load(); /* eslint-disable-next-line */ }, [activeYacht?.id]);

  // Global-search deep link: /app/documents?open=<id> scrolls to the
  // document row/card and briefly highlights it, then strips the query.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || items.length === 0) return;
    const el = document.querySelector(`[data-testid="doc-row-${openId}"]`)
      || document.querySelector(`[data-testid="doc-card-${openId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("bg-sky-100", "transition-colors", "duration-700");
      setTimeout(() => el.classList.remove("bg-sky-100"), 2500);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [items, searchParams, setSearchParams]);

  const startCreate = () => { setEditing(null); setForm({ ...DEFAULT, category: allowedCategories[0] }); setOpen(true); };
  const startEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || "", category: d.category || allowedCategories[0],
      file_path: d.file_path || "", expires_at: (d.expires_at || "").slice(0, 10),
      linked_system: d.linked_system || "", supplier: d.supplier || "", supplier_id: d.supplier_id || "",
      notes: d.notes || "",
      reminder_value: d.reminder_value != null ? String(d.reminder_value) : "",
      reminder_unit: d.reminder_unit || "days",
    });
    setOpen(true);
  };

  const payload = () => ({
    name: form.name, category: form.category,
    expires_at: form.expires_at || null,
    linked_system: form.linked_system || null,
    supplier: form.supplier || null, supplier_id: form.supplier_id || null,
    notes: form.notes || null,
    reminder_value: form.reminder_value === "" ? null : Number(form.reminder_value),
    reminder_unit: form.reminder_value === "" ? null : form.reminder_unit,
  });

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/documents/${editing.id}`, payload());
        toast.success("Document updated");
      } else {
        if (!form.file_path) return toast.error("Upload a file first");
        await api.post("/documents", { ...payload(), file_path: form.file_path, yacht_id: activeYacht.id });
        toast.success("Document saved");
      }
      setForm({ ...DEFAULT, category: allowedCategories[0] }); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const renew = async () => {
    try {
      if (!renewForm.file_path) return toast.error("Upload the new file first");
      await api.post(`/documents/${renewDoc.id}/renew`, {
        file_path: renewForm.file_path,
        expires_at: renewForm.expires_at || null,
      });
      toast.success("Document renewed — previous version kept in history");
      setRenewDoc(null); setRenewForm({ file_path: "", expires_at: "" }); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async () => {
    if (!confirmDoc) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${confirmDoc.id}`);
      toast.success(t("common.deleted"));
      setConfirmDoc(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDeleting(false);
    }
  };

  const downloadUrl = (path) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

  const onPickSupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    setForm({ ...form, supplier_id: id, supplier: s ? s.name : "" });
  };

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("documents.title")}
        right={
          can("document:create") ? (
          <div className="flex items-center gap-2">
            <TourReplayButton tourId="documents" />
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-ink hover:bg-black" onClick={startCreate} data-testid="add-doc-btn"><Plus className="h-4 w-4 mr-2" />{t("documents.upload_doc")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit document" : t("documents.upload_doc")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *" className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="doc-name" /></Field>
                <Field label="Category">
                  <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="doc-cat">
                    {allowedCategories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Expires"><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="doc-expires" /></Field>
                <Field label="Expiry reminder" className="col-span-2">
                  <div className="flex items-center gap-2">
                    <Input type="number" min="0" placeholder="e.g. 2" className="w-24 no-spinner" value={form.reminder_value} onChange={(e) => setForm({ ...form, reminder_value: e.target.value })} data-testid="doc-reminder-value" />
                    <select className="h-9 rounded-md border border-input px-3 text-sm" value={form.reminder_unit} onChange={(e) => setForm({ ...form, reminder_unit: e.target.value })} data-testid="doc-reminder-unit">
                      {REMINDER_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <span className="text-xs text-zinc-500">before expiry — repeats daily until renewed</span>
                  </div>
                </Field>
                <Field label="Linked system">
                  <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.linked_system} onChange={(e) => setForm({ ...form, linked_system: e.target.value })} data-testid="doc-system">
                    <option value="">— none —</option>
                    {SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Supplier">
                  <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.supplier_id} onChange={(e) => onPickSupplier(e.target.value)} data-testid="doc-supplier">
                    <option value="">— none —</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                {!editing && (
                  <Field label="File" className="col-span-2">
                    <FileUploader
                      yacht_id={activeYacht.id}
                      folder="documents"
                      accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.pages"
                      onUploaded={(path) => setForm({ ...form, file_path: path })}
                      label={form.file_path ? "File attached" : "Upload file"}
                      testid="doc-file"
                    />
                  </Field>
                )}
                <Field label="Notes" className="col-span-2"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="doc-notes" /></Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-ink hover:bg-black" disabled={!form.name || (!editing && !form.file_path)} data-testid="save-doc">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          ) : null
        }
      />

      {/* Mobile — stacked document cards (table clips on small screens) */}
      <div className="mt-8 space-y-3 md:hidden" data-testid="docs-mobile-list">
        {items.length === 0 && (
          <div className="rounded-md border border-zinc-200 bg-white p-10 text-center text-zinc-500">
            <FileText className="h-6 w-6 mx-auto text-zinc-300" />
            <div className="mt-2">No documents yet.</div>
          </div>
        )}
        {items.map((d) => {
          const exp = d.expires_at ? new Date(d.expires_at) : null;
          const expSoon = exp && (exp - new Date()) / 86400000 < 30 && (exp - new Date()) >= 0;
          const expired = exp && exp < new Date();
          const versions = d.versions || [];
          return (
            <div key={d.id} className="rounded-md border border-zinc-200 bg-white p-4" data-testid={`doc-card-${d.id}`}>
              <div className="flex items-start gap-2.5">
                <DocThumb path={d.file_path} testid={`doc-thumb-mobile-${d.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium break-words">{d.name}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-zinc-500">
                    <span>{d.category}</span>
                    {d.linked_system && <span>{d.linked_system}</span>}
                    {d.supplier && <span>{d.supplier}</span>}
                  </div>
                </div>
              </div>
              {d.expires_at && (
                <div className={`mt-1 text-xs ${expired ? "text-red-600" : expSoon ? "text-amber-700" : "text-zinc-600"}`}>
                  Expires {(d.expires_at || "").slice(0, 10)}
                  {(expired || expSoon) && <AlertCircle className="inline h-3.5 w-3.5 ml-1" />}
                  {d.reminder_value != null && (
                    <span className="text-zinc-400"> · remind {d.reminder_value} {d.reminder_unit} before</span>
                  )}
                </div>
              )}
              {versions.length > 0 && (
                <button
                  type="button"
                  onClick={() => setHistoryOpen((h) => ({ ...h, [d.id]: !h[d.id] }))}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline"
                  data-testid={`doc-card-history-toggle-${d.id}`}
                >
                  <History className="h-3 w-3" />{versions.length} previous version{versions.length > 1 ? "s" : ""}
                </button>
              )}
              {historyOpen[d.id] && versions.length > 0 && (
                <ul className="mt-2 space-y-1 border-t border-zinc-100 pt-2">
                  {versions.slice().reverse().map((v, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                      <FileText className="h-3 w-3 text-zinc-400 shrink-0" />
                      <a href={downloadUrl(v.file_path)} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Open previous file</a>
                      <span className="text-zinc-400">· replaced {(v.replaced_at || "").slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex items-center gap-1 border-t border-zinc-100 pt-2">
                <a href={downloadUrl(d.file_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-medium mr-auto" data-testid={`open-doc-mobile-${d.id}`}>Open</a>
                {can("document:create") && (
                  <>
                    <Button variant="ghost" size="sm" title="Edit" onClick={() => startEdit(d)} data-testid={`edit-doc-mobile-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" title="Renew — upload new version" onClick={() => { setRenewDoc(d); setRenewForm({ file_path: "", expires_at: "" }); }} data-testid={`renew-doc-mobile-${d.id}`}><RefreshCw className="h-4 w-4" /></Button>
                  </>
                )}
                {can("document:delete") && (
                  <Button variant="ghost" size="sm" title="Delete" onClick={() => setConfirmDoc(d)} data-testid={`del-doc-mobile-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 rounded-md border border-zinc-200 bg-white overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr><Th>Document</Th><Th>Category</Th><Th>Expires</Th><Th>Supplier</Th><Th /></tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-zinc-500">
                <FileText className="h-6 w-6 mx-auto text-zinc-300" />
                <div className="mt-2">No documents yet.</div>
              </td></tr>
            )}
            {items.map((d) => {
              const exp = d.expires_at ? new Date(d.expires_at) : null;
              const expSoon = exp && (exp - new Date()) / 86400000 < 30 && (exp - new Date()) >= 0;
              const expired = exp && exp < new Date();
              const versions = d.versions || [];
              return (
                <>
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <Td className="font-medium">
                    <div className="flex items-start gap-2.5">
                      <DocThumb path={d.file_path} testid={`doc-thumb-${d.id}`} />
                      <div className="min-w-0">
                        <div className="min-w-0 break-words">{d.name}</div>
                        <div className="flex flex-wrap gap-x-2 text-[11px] font-normal text-zinc-500">
                          {d.linked_system && <span>{d.linked_system}</span>}
                          {versions.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setHistoryOpen((h) => ({ ...h, [d.id]: !h[d.id] }))}
                              className="inline-flex items-center gap-1 text-sky-700 hover:underline"
                              data-testid={`doc-history-toggle-${d.id}`}
                            >
                              <History className="h-3 w-3" />{versions.length} previous version{versions.length > 1 ? "s" : ""}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td className="text-zinc-600">{d.category}</Td>
                  <Td className={`text-xs ${expired ? "text-red-600" : expSoon ? "text-amber-700" : "text-zinc-600"}`}>
                    {(d.expires_at || "").slice(0, 10) || "—"}
                    {(expired || expSoon) && <AlertCircle className="inline h-3.5 w-3.5 ml-1" />}
                    {d.reminder_value != null && d.expires_at && (
                      <div className="text-[10px] text-zinc-400">remind {d.reminder_value} {d.reminder_unit} before</div>
                    )}
                  </Td>
                  <Td>{d.supplier || "—"}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <a href={downloadUrl(d.file_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 mr-3" data-testid={`open-doc-${d.id}`}>Open</a>
                    {can("document:create") && (
                      <>
                        <Button variant="ghost" size="sm" title="Edit" onClick={() => startEdit(d)} data-testid={`edit-doc-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" title="Renew — upload new version" onClick={() => { setRenewDoc(d); setRenewForm({ file_path: "", expires_at: "" }); }} data-testid={`renew-doc-${d.id}`}><RefreshCw className="h-4 w-4" /></Button>
                      </>
                    )}
                    {can("document:delete") && (
                      <Button variant="ghost" size="sm" title="Delete" onClick={() => setConfirmDoc(d)} data-testid={`del-doc-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </Td>
                </tr>
                {historyOpen[d.id] && versions.length > 0 && (
                  <tr key={`${d.id}-history`} className="bg-zinc-50/60" data-testid={`doc-history-${d.id}`}>
                    <td colSpan={5} className="px-4 py-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Version history — {d.name}</div>
                      <ul className="space-y-1">
                        {versions.slice().reverse().map((v, i) => (
                          <li key={i} className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                            <FileText className="h-3 w-3 text-zinc-400 shrink-0" />
                            <a href={downloadUrl(v.file_path)} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid={`doc-version-open-${d.id}-${i}`}>Open previous file</a>
                            <span className="text-zinc-400">· replaced {(v.replaced_at || "").slice(0, 10)}{v.replaced_by_name ? ` by ${v.replaced_by_name}` : ""}</span>
                            {v.expires_at && <span className="text-zinc-400">· expired {(v.expires_at || "").slice(0, 10)}</span>}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Renew dialog — new file version + new expiry */}
      <Dialog open={!!renewDoc} onOpenChange={(o) => { if (!o) setRenewDoc(null); }}>
        <DialogContent data-testid="doc-renew-dialog">
          <DialogHeader><DialogTitle>Renew document</DialogTitle></DialogHeader>
          {renewDoc && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">
                Upload the renewed file for <strong>{renewDoc.name}</strong>. The current file is kept in the
                version history, and expiry reminders restart from the new date.
              </p>
              <Field label="New file *">
                <FileUploader
                  yacht_id={activeYacht.id}
                  folder="documents"
                  accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.pages"
                  onUploaded={(path) => setRenewForm((f) => ({ ...f, file_path: path }))}
                  label={renewForm.file_path ? "New file attached" : "Upload new file"}
                  testid="doc-renew-file"
                />
              </Field>
              <Field label="New expiry date">
                <Input type="date" value={renewForm.expires_at} onChange={(e) => setRenewForm((f) => ({ ...f, expires_at: e.target.value }))} data-testid="doc-renew-expires" />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewDoc(null)}>Cancel</Button>
            <Button onClick={renew} className="bg-ink hover:bg-black" disabled={!renewForm.file_path} data-testid="doc-renew-save">Renew</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDoc} onOpenChange={(o) => { if (!o && !deleting) setConfirmDoc(null); }}>
        <AlertDialogContent data-testid="doc-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("documents.confirm_delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDoc?.name ? <span className="font-medium text-zinc-700">{confirmDoc.name}</span> : null}
              {confirmDoc?.name ? " — " : null}
              {t("common.delete_permanent_warning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="doc-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); del(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="doc-delete-confirm-btn"
            >
              {deleting ? t("common.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
